import { useState, useEffect, useMemo } from 'react';
import { CloseIcon, DownloadIcon, RefreshIcon, EditIcon, CopyIcon } from '../../../../components/app-shell/icons';
import ImageViewerModal from './ImageViewerModal';

// ─── data helpers ─────────────────────────────────────────────────────────────

function getOutputs(generation) {
  const { outputs = [], images = [] } = generation;
  if (outputs.length > 0) return outputs;
  return images.map(url => ({ url, type: 'image' }));
}

function getPrompt(generation) {
  const p = generation.input_params;
  if (!p) return generation.prompt || '';
  for (const k of ['positive_prompt', 'prompt', 'text', 'caption']) {
    if (p[k]) return p[k];
  }
  const entry = Object.entries(p).find(([k, v]) =>
    typeof v === 'string' && v.length > 0 && v.length < 500 && !k.includes('url') && !v.startsWith('http')
  );
  return entry ? entry[1] : (generation.prompt || '');
}

function getTimeAgo(date) {
  const s = Math.floor((Date.now() - date) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const abs = d.toLocaleString('en', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return `${getTimeAgo(d)} · ${abs}`;
}

async function downloadMedia(url) {
  try {
    const blob = await fetch(url).then(r => r.blob());
    const ext = blob.type?.split('/')[1]?.split(';')[0]?.replace('jpeg', 'jpg') || 'png';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `zen_${Date.now()}.${ext}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 100);
  } catch { window.open(url, '_blank'); }
}

function buildApiCode(generation, lang) {
  const isWorkflow = !!(generation.isWorkflow || generation.model?.isWorkflow || generation.jobType === 'workflow');
  const modelId = generation.model?.model_id || '';
  const workflowId = generation.model?.workflow_definition_id || '';
  const params = generation.input_params || {};
  const jsonParams = JSON.stringify(params, null, 2);

  if (isWorkflow) {
    const body = JSON.stringify({ workflow_definition_id: workflowId, input_params: params }, null, 2);
    if (lang === 'js') return `const response = await fetch(
  "https://client-api.fluxuro.ai/api/workflows/run",
  {
    method: "POST",
    headers: {
      "Authorization": "Bearer YOUR_API_KEY",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(${body}),
  }
);
const data = await response.json();`;
    if (lang === 'python') return `import requests

response = requests.post(
  "https://client-api.fluxuro.ai/api/workflows/run",
  headers={"Authorization": "Bearer YOUR_API_KEY"},
  json=${body.replace(/: true\b/g, ': True').replace(/: false\b/g, ': False').replace(/: null\b/g, ': None')},
)
data = response.json()`;
    return `curl -X POST "https://client-api.fluxuro.ai/api/workflows/run" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${body}'`;
  }

  // Model job: params spread at top level
  const body = JSON.stringify({ model_id: modelId, ...params }, null, 2);
  if (lang === 'js') return `const response = await fetch(
  "https://client-api.fluxuro.ai/api/models/run-model",
  {
    method: "POST",
    headers: {
      "Authorization": "Bearer YOUR_API_KEY",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(${body}),
  }
);
const data = await response.json();`;
  if (lang === 'python') return `import requests

response = requests.post(
  "https://client-api.fluxuro.ai/api/models/run-model",
  headers={"Authorization": "Bearer YOUR_API_KEY"},
  json=${body.replace(/: true\b/g, ': True').replace(/: false\b/g, ': False').replace(/: null\b/g, ': None')},
)
data = response.json()`;
  return `curl -X POST "https://client-api.fluxuro.ai/api/models/run-model" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${body}'`;
}

// ─── style helpers ─────────────────────────────────────────────────────────────

const LABEL = { fontSize: 10, color: 'rgba(255,255,255,0.55)', display: 'block', marginBottom: 8 };
const DIVIDER = { height: 1, background: 'rgba(255,255,255,0.06)' };

function navBtnStyle(side) {
  return {
    position: 'absolute', [side]: 12, top: '50%', transform: 'translateY(-50%)',
    width: 36, height: 36, borderRadius: '50%',
    background: 'rgba(15,15,17,0.85)', backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 18,
  };
}

function thumbBtnStyle(active) {
  return {
    flexShrink: 0, width: 80, height: 56, borderRadius: 8, overflow: 'hidden',
    cursor: 'pointer', padding: 1, position: 'relative',
    border: `2px solid ${active ? 'rgb(255,194,181)' : 'transparent'}`,
    background: active ? 'rgba(255,126,103,0.1)' : 'transparent',
  };
}

function ghostPillBtn(h) {
  return {
    flex: 1, height: h, background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 999,
    fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  };
}

function langTabStyle(active) {
  return {
    fontSize: 9, padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
    background: active ? 'rgba(99,102,241,0.15)' : 'transparent',
    color: active ? 'rgb(165,180,252)' : 'rgba(255,255,255,0.55)',
    border: active ? '1px solid rgba(99,102,241,0.3)' : '1px solid transparent',
  };
}

// ─── small components ──────────────────────────────────────────────────────────

function QuickAction({ icon, label, onClick, active }) {
  return (
    <button onClick={onClick} style={{ flex: 1, height: 32, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 9, color: active ? '#10B981' : 'rgba(255,255,255,0.72)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', fontSize: 12, gap: 12 }}>
      <span style={{ color: 'rgba(255,255,255,0.55)', flexShrink: 0 }}>{label}</span>
      <span style={{ color: '#fff', textAlign: 'right', fontFamily: mono ? 'ui-monospace,monospace' : undefined }}>{value}</span>
    </div>
  );
}

// ─── left panel ───────────────────────────────────────────────────────────────

function LeftPanel({ outputs, active, setActive, onUseAsInput, inputImages, imageParams }) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIdx, setViewerIdx] = useState(0);
  const cur = outputs[active];
  const multi = outputs.length > 1;
  const imageUrls = outputs.filter(o => o.type !== 'video' && o.type !== 'text' && o.url).map(o => o.url);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'rgb(7,7,8)', minWidth: 0 }}>
      {/* Main viewer */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, position: 'relative', overflow: 'hidden' }}>
        {cur && (
          <div
            style={{ position: 'relative', maxWidth: '100%', width: '100%', borderRadius: 14, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden', cursor: cur.url && cur.type !== 'video' && cur.type !== 'text' ? 'zoom-in' : 'default' }}
            onClick={() => { if (cur.url && cur.type !== 'video' && cur.type !== 'text') { setViewerIdx(imageUrls.indexOf(cur.url)); setViewerOpen(true); } }}
          >
            {cur.type === 'video' ? (
              <video src={cur.url} controls autoPlay loop muted style={{ width: '100%', display: 'block', maxHeight: 'calc(100vh - 320px)', background: '#000' }} />
            ) : cur.type === 'text' ? (
              <div style={{ padding: 24, fontSize: 14, color: 'rgba(255,255,255,.85)', lineHeight: 1.65, whiteSpace: 'pre-wrap', minHeight: 200, maxHeight: 500, overflowY: 'auto' }}>
                {cur.content}
              </div>
            ) : (
              <img src={cur.url} alt="" style={{ width: '100%', display: 'block', maxHeight: 'calc(100vh - 320px)', objectFit: 'contain' }} />
            )}
          </div>
        )}

        {multi && active > 0 && (
          <button onClick={e => { e.stopPropagation(); setActive(i => i - 1); }} style={navBtnStyle('left')}>←</button>
        )}
        {multi && active < outputs.length - 1 && (
          <button onClick={e => { e.stopPropagation(); setActive(i => i + 1); }} style={navBtnStyle('right')}>→</button>
        )}
      </div>

      {/* Batch thumbnail strip */}
      {multi && (
        <div style={{ flexShrink: 0, background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '12px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>Batch · {outputs.length} outputs</span>
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
            {outputs.slice(0, 4).map((o, i) => (
              <button key={i} onClick={() => setActive(i)} style={thumbBtnStyle(i === active)}>
                {o.type === 'video'
                  ? <video src={o.url} muted style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 5 }} />
                  : o.url
                  ? <img src={o.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 5, display: 'block' }} />
                  : <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', borderRadius: 5, background: 'rgba(255,255,255,0.04)', fontSize: 12 }}>T</div>
                }
                <span style={{ position: 'absolute', top: 3, left: 3, fontSize: 9, color: 'rgba(255,255,255,0.8)' }}>v{i + 1}</span>
              </button>
            ))}
            {outputs.length > 4 && (
              <div style={{ flexShrink: 0, width: 80, height: 56, borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>
                +{outputs.length - 4}
              </div>
            )}
          </div>
        </div>
      )}

      <ImageViewerModal
        isOpen={viewerOpen}
        images={imageUrls}
        inputImages={inputImages}
        imageParams={imageParams}
        initialIndex={viewerIdx}
        onClose={() => setViewerOpen(false)}
        onUseAsInput={onUseAsInput}
      />
    </div>
  );
}

// ─── right panel ──────────────────────────────────────────────────────────────

function RightPanel({ generation, outputs, activeOutput, prompt, onReuse, onIterateWith, onEditImage, onClose, formatCost, onUseAsInput }) {
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [refViewerOpen, setRefViewerOpen] = useState(false);
  const [refViewerImages, setRefViewerImages] = useState([]);
  const [copiedJobId, setCopiedJobId] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [apiLang, setApiLang] = useState('curl');

  const model = generation.model;
  const cost = generation.cost;
  const jobId = generation.id || generation._id || generation.job_id || '';
  const shortJobId = jobId.length > 14 ? jobId.slice(0, 8) + '…' + jobId.slice(-3) : jobId;
  const outputType = outputs[0]?.type || 'image';

  const inputImages = (() => {
    const p = generation.input_params || {};
    return Object.entries(p)
      .filter(([, v]) => {
        const isUrl = (typeof v === 'string' && v.startsWith('http')) ||
          (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string' && v[0].startsWith('http'));
        return isUrl;
      })
      .flatMap(([, v]) => Array.isArray(v) ? v : [v])
      .filter(Boolean);
  })();

  const displayParams = Object.entries(generation.input_params || {}).filter(([k, v]) => {
    if (['webhookURL', 'customTaskUUID'].includes(k)) return false;
    if (typeof v === 'string' && v.startsWith('http')) return false;
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string' && v[0].startsWith('http')) return false;
    if (k.toLowerCase().includes('prompt')) return false;
    return v !== null && v !== undefined && v !== '';
  });

  const code = buildApiCode(generation, apiLang);

  function copyPrompt() {
    navigator.clipboard.writeText(prompt);
    setCopiedPrompt(true);
    setTimeout(() => setCopiedPrompt(false), 1500);
  }
  function copyJobId() {
    navigator.clipboard.writeText(jobId);
    setCopiedJobId(true);
    setTimeout(() => setCopiedJobId(false), 1500);
  }
  function copyCode() {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 1500);
  }

  const currentOutputUrl = outputs[activeOutput]?.url;

  return (
    <aside style={{ width: 380, flexShrink: 0, borderLeft: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.016)', overflowY: 'auto' }}>
      <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── Action buttons ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Iterate on this = use output as image input */}
          <button
            onClick={() => { if (currentOutputUrl) { onIterateWith?.(currentOutputUrl); onClose?.(); } }}
            disabled={!currentOutputUrl}
            style={{ height: 40, width: '100%', background: currentOutputUrl ? 'linear-gradient(135deg,#FF7E67,#FF4D8D)' : 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 999, fontSize: 13, fontWeight: 600, color: '#fff', cursor: currentOutputUrl ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: currentOutputUrl ? '0 6px 20px -6px rgba(255,77,141,.5)' : 'none' }}
          >
            <EditIcon s={13} /> Iterate on this
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => outputs.forEach(o => o.url && downloadMedia(o.url))}
              style={ghostPillBtn(36)}
            >
              <DownloadIcon s={12} /> Download
            </button>
            {/* Reuse settings = restore original params */}
            <button
              onClick={() => { onReuse?.(generation); onClose?.(); }}
              style={ghostPillBtn(36)}
            >
              <RefreshIcon s={12} /> Reuse settings
            </button>
          </div>
        </div>

        {/* ── Quick actions ── */}
        <div style={{ display: 'flex', gap: 6 }}>
          {currentOutputUrl && onEditImage && (
            <QuickAction
              icon={<EditIcon s={13} />}
              label="Use as ref"
              onClick={() => { onEditImage(currentOutputUrl); onClose?.(); }}
            />
          )}
          <QuickAction icon={<CopyIcon s={13} />} label="Copy params" onClick={copyPrompt} active={copiedPrompt} />
          <QuickAction icon={<DownloadIcon s={13} />} label="Download all" onClick={() => outputs.forEach(o => o.url && downloadMedia(o.url))} />
        </div>

        <div style={DIVIDER} />

        {/* ── Prompt ── */}
        {prompt && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>Prompt</span>
              <button onClick={copyPrompt} style={{ fontSize: 11, color: copiedPrompt ? '#10B981' : 'rgba(255,255,255,0.36)', background: 'none', border: 'none', cursor: 'pointer', padding: '1px 6px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <CopyIcon s={10} /> {copiedPrompt ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 }}>{prompt}</p>
          </div>
        )}

        {/* ── Model ── */}
        {model && (
          <div>
            <span style={LABEL}>Model</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 30, height: 30, flexShrink: 0, background: 'linear-gradient(135deg,#FF4D8D,#A78BFA)', border: '1px solid rgba(255,77,141,0.4)', borderRadius: 8, display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800, color: '#fff' }}>
                {(model.model_name || model.name || 'M').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{model.model_name || model.name}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{model.provider || 'URO'}</div>
              </div>
              <div style={{ height: 22, padding: '0 9px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,77,141,0.3)', borderRadius: 999, fontSize: 10, color: 'rgb(255,194,181)', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {outputType.charAt(0).toUpperCase() + outputType.slice(1)}
              </div>
            </div>
          </div>
        )}

        {/* ── Parameters ── */}
        {displayParams.length > 0 && (
          <div>
            <span style={LABEL}>Parameters</span>
            <div style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, overflow: 'hidden' }}>
              {displayParams.map(([k, v], i) => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderBottom: i < displayParams.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', fontSize: 12 }}>
                  <span style={{ color: 'rgba(255,255,255,0.55)', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</span>
                  <span style={{ color: k.toLowerCase().includes('negative') ? 'rgb(255,177,200)' : '#fff', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {String(v)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Reference images — clickable ── */}
        {inputImages.length > 0 && (
          <div>
            <span style={LABEL}>References ({inputImages.length})</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {inputImages.map((url, i) => (
                <div key={i} onClick={() => { setRefViewerImages(inputImages); setRefViewerOpen(true); }} style={{ height: 46, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'zoom-in' }}>
                  <img src={url} alt="" style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600 }}>Reference {i + 1}</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.36)' }}>Click to view</div>
                  </div>
                </div>
              ))}
            </div>
            <ImageViewerModal isOpen={refViewerOpen} images={refViewerImages} onClose={() => setRefViewerOpen(false)} onUseAsInput={onUseAsInput} />
          </div>
        )}

        {/* ── Details ── */}
        <div>
          <span style={LABEL}>Details</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {cost != null && (
              <DetailRow label="Cost" value={`${formatCost ? formatCost(cost) : Number(cost).toPrecision(3)} URO`} />
            )}
            {(generation.createdAt || generation.created_at) && (
              <DetailRow label="Created" value={formatTime(generation.createdAt || generation.created_at)} />
            )}
            {jobId && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: 'rgba(255,255,255,0.55)' }}>Job ID</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: 'ui-monospace,monospace', color: '#fff' }}>{shortJobId}</span>
                  <button onClick={copyJobId} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedJobId ? '#10B981' : 'rgba(255,255,255,0.36)', padding: 0, display: 'grid', placeItems: 'center' }}>
                    <CopyIcon s={12} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Reproduce via API ── */}
        <div>
          <span style={LABEL}>Reproduce via API</span>
          <div style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ height: 28, padding: '0 10px', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {[['curl', 'cURL'], ['js', 'JS'], ['python', 'Python']].map(([id, label]) => (
                  <button key={id} onClick={() => setApiLang(id)} style={langTabStyle(id === apiLang)}>{label}</button>
                ))}
              </div>
              <button onClick={copyCode} style={{ fontSize: 10, color: copiedCode ? '#10B981' : 'rgba(255,255,255,0.55)', background: 'none', border: 'none', cursor: 'pointer', padding: '1px 6px', display: 'flex', alignItems: 'center', gap: 3 }}>
                <CopyIcon s={10} /> {copiedCode ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre style={{ margin: 0, padding: 10, fontSize: 10.5, lineHeight: 1.55, color: 'rgb(229,231,235)', overflowX: 'auto', fontFamily: 'ui-monospace,SFMono-Regular,monospace', maxHeight: 220, overflowY: 'auto' }}>
              {code}
            </pre>
          </div>
        </div>

      </div>
    </aside>
  );
}

// ─── main modal ───────────────────────────────────────────────────────────────

function navArrowStyle(disabled) {
  return {
    width: 28, height: 28, background: disabled ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7,
    color: disabled ? 'rgba(255,255,255,0.2)' : '#fff',
    cursor: disabled ? 'default' : 'pointer', display: 'grid', placeItems: 'center', fontSize: 14,
    flexShrink: 0,
  };
}

export default function ZenGenerationModal({ generation, generations = [], onClose, onReuse, onIterateWith, onEditImage, onUseAsInput, formatCost, imageParams = [] }) {
  const [activeOutput, setActiveOutput] = useState(0);
  const [currentGen, setCurrentGen] = useState(generation);

  // Sync if prop changes externally
  useEffect(() => { setCurrentGen(generation); setActiveOutput(0); }, [generation]);

  // Navigable = completed generations with outputs
  const navigable = useMemo(() =>
    generations.filter(g => g.status === 'completed' && (g.outputs?.length > 0 || g.images?.length > 0)),
    [generations]
  );
  const currentIdx = navigable.findIndex(g => g.id === currentGen.id);
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx < navigable.length - 1;

  // Lift inputImages from generation so LeftPanel can pass to ImageViewerModal
  const inputImages = useMemo(() => {
    const p = currentGen.input_params || {};
    return Object.entries(p)
      .filter(([, v]) => {
        const isUrl = (typeof v === 'string' && v.startsWith('http')) ||
          (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string' && v[0].startsWith('http'));
        return isUrl;
      })
      .flatMap(([, v]) => Array.isArray(v) ? v : [v])
      .filter(Boolean);
  }, [currentGen]);

  // Keyboard: ESC closes, arrows navigate
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { onClose?.(); return; }
      if (e.key === 'ArrowLeft' && hasPrev) { setCurrentGen(navigable[currentIdx - 1]); setActiveOutput(0); }
      if (e.key === 'ArrowRight' && hasNext) { setCurrentGen(navigable[currentIdx + 1]); setActiveOutput(0); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, hasPrev, hasNext, currentIdx, navigable]);

  if (!currentGen) return null;

  const outputs = getOutputs(currentGen);
  const prompt = getPrompt(currentGen);
  const model = currentGen.model;
  const status = currentGen.status;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, padding: 32, display: 'flex' }}>
      {/* Backdrop — click to close */}
      <div
        style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,5,0.82)', backdropFilter: 'blur(28px)' }}
        onClick={onClose}
      />

      {/* Modal card */}
      <div style={{
        position: 'relative', flex: 1, display: 'flex', flexDirection: 'column',
        background: 'rgba(10,10,12,0.94)', backdropFilter: 'blur(28px)',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden',
        boxShadow: '0 40px 100px -20px rgba(0,0,0,0.9)',
      }}>

        {/* Header */}
        <div style={{ height: 56, padding: '0 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={onClose} title="Close (Esc)" style={{ width: 30, height: 30, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <CloseIcon s={13} />
            </button>
            <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.08)' }} />
            {/* Prev / Next navigation */}
            <button
              onClick={() => { if (hasPrev) { setCurrentGen(navigable[currentIdx - 1]); setActiveOutput(0); } }}
              title="Previous (←)"
              style={navArrowStyle(!hasPrev)}
              disabled={!hasPrev}
            >‹</button>
            <button
              onClick={() => { if (hasNext) { setCurrentGen(navigable[currentIdx + 1]); setActiveOutput(0); } }}
              title="Next (→)"
              style={navArrowStyle(!hasNext)}
              disabled={!hasNext}
            >›</button>
            {navigable.length > 1 && (
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', padding: '0 2px' }}>
                {currentIdx + 1} / {navigable.length}
              </span>
            )}
            <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.08)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <span style={{ color: 'rgba(255,255,255,0.55)' }}>Generation</span>
              <span style={{ color: 'rgba(255,255,255,0.36)' }}>/</span>
              <span style={{ fontWeight: 600 }}>{model?.model_name || model?.name || 'Unknown'}</span>
            </div>
            {status && (
              <div style={{ height: 22, padding: '0 9px', background: 'rgba(255,126,103,0.08)', border: '1px solid rgba(255,126,103,0.25)', borderRadius: 999, fontSize: 11, color: 'rgb(255,194,181)', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: status === 'completed' ? '#10B981' : '#FF7E67', flexShrink: 0 }} />
                {status}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {outputs.length > 1 && (
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', padding: '0 4px' }}>
                {activeOutput + 1} / {outputs.length}
              </span>
            )}
            {outputs.some(o => o.url) && (
              <button
                onClick={() => { const o = outputs[activeOutput]; if (o?.url) downloadMedia(o.url); }}
                title="Download"
                style={{ width: 30, height: 30, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
              >
                <DownloadIcon s={13} />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <LeftPanel outputs={outputs} active={activeOutput} setActive={setActiveOutput} onUseAsInput={onUseAsInput} inputImages={inputImages} imageParams={imageParams} />
          <RightPanel
            generation={currentGen}
            outputs={outputs}
            activeOutput={activeOutput}
            prompt={prompt}
            onReuse={onReuse}
            onIterateWith={onIterateWith}
            onEditImage={onEditImage}
            onClose={onClose}
            formatCost={formatCost}
            onUseAsInput={onUseAsInput}
          />
        </div>

      </div>
    </div>
  );
}
